import { TranslateService } from '@ngx-translate/core';
import {
    Extension
} from "ng2-adsk-forge-viewer";
import { Model } from 'src/app/interfaces/api/model';
import { Zone } from 'src/app/interfaces/api/zone';
import { Config } from 'src/app/interfaces/shared/config';
import { ExtendedMesh } from 'src/app/interfaces/shared/extendMesh';
import { ZoneService } from 'src/app/services/api/zones/zone.service';
import * as THREE from 'three';
import { ZoneShape } from '../shapes/zone';
import { ZoneDockingPanel } from './zone-docking-panel';

export class ZoneExtension extends Extension {
    public static override extensionName: string = 'ZoneExtension';
    private subToolbar: any;
    private dockingPanel: ZoneDockingPanel | null = null;
    public translate!: TranslateService;
    public zoneService!: ZoneService;
    public model: Model | null = null;

    callback?: (() => void);

    panel: any = null;
    zones: ZoneShape[] = [];
    zoneMeshes: Record<string, THREE.Mesh | ExtendedMesh> = {};
    colors = Config.colors;
    zoneButton!: any;

    zoneMaterials: Record<string, THREE.MeshPhongMaterial> = {
        default: new THREE.MeshPhongMaterial({
            color: this.colors.default,
            transparent: true,
            opacity: 0.75
        }),
        danger: new THREE.MeshPhongMaterial({
            color: this.colors.danger,
            transparent: true,
            opacity: 0.75
        }),
        primary: new THREE.MeshPhongMaterial({
            color: this.colors.primary,
            transparent: true,
            opacity: 0.75
        }),
        warning: new THREE.MeshPhongMaterial({
            color: this.colors.warning,
            transparent: true,
            opacity: 0.75
        }),
        success: new THREE.MeshPhongMaterial({
            color: this.colors.default,
            transparent: true,
            opacity: 0.75
        })
    }

    private zonesSub?: any;

    public activate(): any { }

    public deactivate(): any { }

    public load(): any {
        this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, () => {
            this.createUI();
        })

        return true;
    }

    public unload(): any {
        if (this.zonesSub)
            this.zonesSub.unsubscribe();

        return true;
    }

    public createUI() {
        // create toolbar buttons
        var button = new Autodesk.Viewing.UI.Button('zones-btn');
        this.zoneButton = button;

        button.addClass('zones-btn');
        button.setToolTip('zones');
        button.setIcon('zones-btn-icon');

        button.onMouseOver = () => {
            button.setIcon('hover-zones-btn-icon');
        }

        button.onMouseOut = () => {
            button.setIcon('zones-btn-icon');
        }

        button.onClick = () => {
            this.onToolbarButtonClicked();
            this.updateButtonActiveState();
        }

        this.subToolbar = this.viewer.toolbar.getControl('unifield-toolbar')
        if (!this.subToolbar) {
            this.subToolbar = new Autodesk.Viewing.UI.ControlGroup('unifield-toolbar');
            this.viewer.toolbar.addControl(this.subToolbar);
        }

        this.subToolbar.addControl(button);
        const styleId = 'zones-extension-style';

        if (!document.getElementById(styleId)) {
            const link = document.createElement('link');
            link.id = styleId;
            link.rel = 'stylesheet';
            link.href = 'viewer/zones/zone-extension/zone-extension.css';
            document.head.appendChild(link);
        }
    };

    public init() {
        this.initOverlay();

        this.zonesSub = this.zoneService.getZones({}).subscribe((zones: Zone[]) => {
            this.zones = zones
                .map((zone: Zone) => this.setZone(zone))
                .filter((zoneShape): zoneShape is ZoneShape => !!zoneShape);

            if (this.dockingPanel && this.dockingPanel.isVisible()) {
                this.dockingPanel.zones = this.zones;
                this.dockingPanel.init();
            }
        });
    }

    initOverlay() {
        try {
            Object.keys(this.colors).forEach(c => {
                let material: any = new THREE.MeshPhongMaterial({
                    color: this.colors[c],
                    transparent: true, opacity: 0.5
                });

                this.viewer.impl.createOverlayScene("zone_" + c, material);
            });
        } catch (e) {
            console.error(e)
        }
    }

    setZone(zone: Zone): ZoneShape | undefined {
        try {
            var zoneMesh: THREE.Mesh | ExtendedMesh | null = null;

            zoneMesh = this.zoneMeshes[zone.id] || null;
            var zRadians = this.model?.origin?.zAngle ? this.model.origin.zAngle * (Math.PI / 180) : 0;
            var yRadians = this.model?.origin?.yAngle ? this.model.origin.yAngle * (Math.PI / 180) : 0;
            var xRadians = this.model?.origin?.xAngle ? this.model.origin.xAngle * (Math.PI / 180) : 0;

            switch (zone.type) {
                case 'plane':
                    zoneMesh = new THREE.Mesh(new THREE.BoxGeometry(zone.width || 1, zone.height || 1, 0.1), this.zoneMaterials[zone.color]);

                    if (zone.z) {
                        zoneMesh.position.z = zone.z;
                    }

                    zoneMesh.rotateOnAxis(new THREE.Vector3(1, 0, 0), xRadians);
                    zoneMesh.rotateOnAxis(new THREE.Vector3(0, 1, 0), yRadians);
                    zoneMesh.rotateOnAxis(new THREE.Vector3(0, 0, 1), zRadians);
                    zoneMesh.updateMatrix();
                    break;

                case 'circle':
                    zoneMesh = new THREE.Mesh(new THREE.CylinderGeometry(zone.radius || 1, zone.radius || 1, 0.1, 24), this.zoneMaterials[zone.color]);

                    if (zone.z) {
                        zoneMesh.position.z = zone.z;
                    }

                    zoneMesh.rotateOnAxis(new THREE.Vector3(1, 0, 0), xRadians);
                    zoneMesh.rotateOnAxis(new THREE.Vector3(0, 1, 0), yRadians);
                    zoneMesh.rotateOnAxis(new THREE.Vector3(0, 0, 1), zRadians);
                    zoneMesh.updateMatrix();
                    break;

                case 'polygon':
                case 'geometry':
                    var zoneObj = new ZoneShape({
                        viewer: this.viewer,
                        model: this.model!,
                        polygon: zone.polygon || [],
                        type: zone.type || 'polygon',
                        z: zone.z || 0,
                        height: zone.height || 1,
                        name: zone.name || 'Zone',
                        number: zone.number || 0,
                        color: zone.color || 'primary',
                        alpha: zone.alpha || 0.5,
                        id: zone.id,
                        visible: zone.visible !== false,
                        center: zone.position || { x: 0, y: 0, z: 0 },
                        geometry: zone.geometry
                    });

                    if (zone.geometry && zone.geometry.position && zone.geometry.position.length > 2) {
                        zoneObj.drawGeometry(true);
                    } else if (zone.polygon && zone.polygon.length > 2) {
                        zoneObj.draw(true);
                    }

                    this.zoneMeshes[zone.id] = zoneObj.mesh;
                    return zoneObj;

                default:
                    return;
            }

            if (zoneMesh) {
                (zoneMesh as any).zone = zone;
            }

            if (zoneMesh) {
                this.zoneMeshes[zone.id] = zoneMesh;
            }
        } catch (e) {
            console.error(e)
        }

        return undefined;
    }

    dockPanel() {
        if (!this.dockingPanel) {
            this.dockingPanel = new ZoneDockingPanel(this.viewer, this.viewer.container, 'zonecontainer', 'Zones', {
                zoneService: this.zoneService,
                translate: this.translate,
                model: this.model,
                zones: this.zones
            });
        }

        // Wait for the DOM is ready
        setTimeout(() => {
            const titleDiv = (this.dockingPanel as any)?.container?.querySelector('.docking-panel-title');

            if (titleDiv && !titleDiv.textContent?.trim()) {
                titleDiv.textContent = "Zones";
            }
        }, 0);

        this.dockingPanel.setVisible(true);
        this.updateButtonActiveState();
    }

    onToolbarButtonClicked() {
        if (!this.dockingPanel) {
            this.dockPanel();
        } else {
            const container = (this.dockingPanel as any).container as HTMLElement;
            const isVisible = container?.style.display !== 'none';
            this.dockingPanel.setVisible(!isVisible);
            this.updateButtonActiveState();
        }
    }

    private updateButtonActiveState() {
        const container = (this.dockingPanel as any)?.container as HTMLElement;
        const active = !!this.dockingPanel && container?.style.display !== 'none';

        if (this.zoneButton) {
            this.zoneButton.setState(active ? Autodesk.Viewing.UI.Button.State.ACTIVE : Autodesk.Viewing.UI.Button.State.INACTIVE);
        }

        const buttonElement = document.getElementById('zones-btn');
        if (buttonElement) {
            buttonElement.classList.toggle('active', active);
            buttonElement.classList.toggle('inactive', !active);
        }
    }
}
